import { useState, useMemo, useEffect, useCallback} from "react";
import { useAuth } from "../../../contexts/AuthContext";
import {
  useBiddingInfoGetInfoForBuyerQuery,
  useBiddingInfoGetInfoListForBuyerQuery,
  useUpsertBidMutation,
} from "../../../hooks/useBiddingInfoQuery";
import {
  ListingCategoryConst,
  BiddingItemStatusConst,
  PrefTypeConst,
} from "../../../constants";
import type { UpdateBiddingDataRequest, BiddingInfo } from "../../../types";
import { Check, Heart, Search, X } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import { NumericFormat } from "react-number-format";
import { NumericKeypad } from "../../../components/common/NumericKeypad";
import { BiddingItemNumberDetail } from "../../../components/buyer/BiddingInput/BiddingItemNumberDetail";
import Big from "big.js";
import { useItemFilters } from "../../../contexts/ItemFilterContext";
import { useMasters } from "../../../contexts/MastersContext";
import { useGetBiddingDateForApplyQuery, useGetSaleBiddingDateForApplyQuery } from "../../../hooks/useCalendarQuery";
import { useGetFavoritesQuery, useToggleFavoriteMutation } from "../../../hooks/useBiddingItemsQuery";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useBiddingConfigQuery } from "../../../hooks/useSystemMasterQuery";
import { useAppDialog2 } from "../../../hooks/useAppDialog2";
import { useLocation, useNavigate } from 'react-router-dom';
import { useRef } from 'react';

dayjs.extend(utc);
dayjs.extend(timezone);

// デフォルトのタイムゾーンを日本時間(JST)に設定
dayjs.tz.setDefault("Asia/Tokyo");

type Props = {
  isModal?: boolean;              // ダイアログとして開いているか？
  initialItem?: BiddingInfo;
  onClose?: () => void;           // 閉じる時の処理
};

const BiddingInputNumberPage = ({ isModal = false, initialItem, onClose }: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isFromListRef = useRef(false);

  // 1. データ準備
  const { currentUser } = useAuth();
  const { itemFilters, updateFiltersAndNavigate } = useItemFilters();
  const { isMastersLoading } = useMasters(); 
  
  const [isInitialized, setIsInitialized] = useState(false);
  const { data: activeBiddingDate, isLoading: isDateLoading } = useGetBiddingDateForApplyQuery(currentUser?.loginId);
  // 🔒 D-0127（見積No.8）: 入札会の入札日も常時取得しておく（番号確定時に出品区分を判別し、対応する日付を選ぶ＝INV-QBS-3）
  const { data: activeSaleBiddingDate, isLoading: isSaleDateLoading } = useGetSaleBiddingDateForApplyQuery(currentUser?.loginId, !!currentUser?.loginId);
  const { data: config } = useBiddingConfigQuery();
  const upsertBidMutation = useUpsertBidMutation();

  const pageTitle = "かんたん入札";

  // ダイアログ用フック
  const { showConfirm, OverlayElement, isOpen: isConfirmOpen } = useAppDialog2();

  // State
  const [targetBiddingNumber, setTargetBiddingNumber] = useState(""); //入札番号
  const [inputPrice, setInputPrice] = useState(""); //入札額
  const [foundItem, setFoundItem] = useState<BiddingInfo | null>(null); //出品データ
  const [isFavoritesOpen, setIsFavoritesOpen] = useState(false);//お気に入り管理
  const [activeField, setActiveField] = useState<"biddingnumber" | "price">("biddingnumber",); //フォーカス位置
  const [isFromListOrFavorite, setIsFromListOrFavorite] = useState(false);

  //----------------
  // 1. 初期化・遷移元判定（最優先で実行）
  //----------------
  useEffect(() => {
    // 画面遷移で渡された場合は location.state?.initialItem から取得
    const passedItem = isModal ? initialItem : location.state?.initialItem;
    
    // まだ初期セットされていなければセットする
    if (passedItem && !isFromListRef.current) {
      isFromListRef.current = true;

      // 🔒 P-0043: 未採番（入札番号なし）の出品は入札できないため、案内して閉じる
      if (!passedItem.biddingNumber) {
        toast.error("入札番号がありません。管理者に問い合わせてください。");
        if (isModal && onClose) {
          onClose();
        } else {
          navigate(-1);
        }
        if (!isModal) window.history.replaceState({}, document.title);
        return;
      }

      setIsFromListOrFavorite(true);  // 一覧から来たよという目印
      setTargetBiddingNumber(passedItem.biddingNumber);
      setFoundItem(passedItem);   // 通信せずに直接セットする！
      setActiveField("price");    // すぐに金額入力へフォーカス
      if (!isModal) window.history.replaceState({}, document.title);
    }
  }, [initialItem, location.state, isModal, navigate, onClose]);

  //----------------
  // 1. 初期化ロジック 
  //----------------
  useEffect(() => {
    // 読み込み中や認証前は待機
    if (isDateLoading || isMastersLoading || !currentUser) return;
    if (isInitialized) return;

    const newFilters = { ...itemFilters };
    let hasChanged = false;

    // A. 入札日の設定 (APIからの日付 or 今日)
    if (!newFilters.biddingDate) {
      newFilters.biddingDate = activeBiddingDate ? String(activeBiddingDate) : dayjs().tz().format("YYYYMMDD");
      hasChanged = true;
    }

    // B. 出品区分
    if (!newFilters.listingCategory) {
      newFilters.listingCategory = ListingCategoryConst.BIDDING;
      hasChanged = true;
    }

    if (hasChanged) {
      updateFiltersAndNavigate(newFilters);
    }

    setIsInitialized(true);
  }, [isDateLoading, isMastersLoading, currentUser, itemFilters, isInitialized, activeBiddingDate, updateFiltersAndNavigate]);

  //ここでデータ取得
  const filters = useMemo(() => ({
    biddingDate: itemFilters.biddingDate || (activeBiddingDate ? String(activeBiddingDate) : ""),
    listingCategory: itemFilters.listingCategory || ListingCategoryConst.BIDDING,
    // TODO(入札会-No.8): 本画面（かんたん入札）は県区分を利用者に選ばせず、**県内と県外を別々に2回**
    //   問い合わせて合算する作り（下の internalResponse / externalResponse）。そのため直下の
    //   `|| INTERNAL` は2つの呼び出しで必ず上書きされ、実質使われていない。
    //   🔒 入札会に対応するには「変換の修正」では足りず、**入札会（県区分 0）を3系列目として
    //   問い合わせるか**という設計判断が要る（P-0035・P-0040）。
    prefectureType: itemFilters.prefectureType || PrefTypeConst.INTERNAL,
    status: BiddingItemStatusConst.CONFIRMED,
  }), [itemFilters.biddingDate, itemFilters.listingCategory, itemFilters.prefectureType, activeBiddingDate]);

  // 有効な日付がある場合のみ検索を許可
  const isSearchEnabled = !!filters.biddingDate && filters.biddingDate !== "undefined";

  // 🔒 D-0127（見積No.8・INV-QBS-1）: 4桁の入力番号の先頭が"0"なら入札会、それ以外は通常入札と判定する。
  //   D-0123（4桁ゼロ埋め統一）により、入札会は"0101"〜"0999"・通常入札は"1000"〜で数値域が重複しないため一意に判定できる。
  const isSaleNumber = targetBiddingNumber.length === 4 && targetBiddingNumber[0] === "0";
  // 🔒 INV-QBS-3: 検索に使う入札日は、判定された出品区分に対応する側を使う
  const searchBiddingDate = isSaleNumber
    ? (activeSaleBiddingDate ? String(activeSaleBiddingDate) : "")
    : filters.biddingDate;

  // ① お気に入り判定用（一覧API）
  // 県内データの取得
  const { data: internalResponse, isLoading: isInternalLoading } = useBiddingInfoGetInfoListForBuyerQuery(
    { ...filters, prefectureType: PrefTypeConst.INTERNAL },
    currentUser?.loginId ?? "",
    { enabled: isInitialized && isSearchEnabled && isFavoritesOpen }
  );
  
  // 県外データの取得
  const { data: externalResponse, isLoading: isExternalLoading } = useBiddingInfoGetInfoListForBuyerQuery(
    { ...filters, prefectureType: PrefTypeConst.EXTERNAL },
    currentUser?.loginId ?? "",
    { enabled: isInitialized && isSearchEnabled && isFavoritesOpen }
  );

  // 🔒 D-0128（見積No.8・INV-QBS-5）: 入札会データの取得（3系列目・県区分の概念を持たないためNONE指定）
  const isSaleDateEnabled = !!activeSaleBiddingDate;
  const { data: saleResponse, isLoading: isSaleListLoading } = useBiddingInfoGetInfoListForBuyerQuery(
    {
      ...filters,
      listingCategory: ListingCategoryConst.SALE,
      prefectureType: PrefTypeConst.NONE,
      biddingDate: activeSaleBiddingDate ? String(activeSaleBiddingDate) : "",
    },
    currentUser?.loginId ?? "",
    { enabled: isInitialized && isSaleDateEnabled && isFavoritesOpen }
  );

  // 3系列の通信が終わるまで待機用
  const isListLoading = isInternalLoading || isExternalLoading || isSaleListLoading;
  
  // 3. API通信のスキップ制御
  // 「4桁入力されている」 かつ 「一覧やお気に入りからの自動セットではない（＝手打ちされた）」
  // かつ「（入札会の場合は）入札会の入札日が取得できている」時だけ通信する
  const shouldFetch = isInitialized && targetBiddingNumber.length === 4 && !isFromListOrFavorite && !!searchBiddingDate;

  // ② メインの検索用（1件取得API）
  const {
    data: searchResponse,
    error: searchError
  } = useBiddingInfoGetInfoForBuyerQuery(
    searchBiddingDate,
    targetBiddingNumber,
    currentUser?.loginId ?? "",
    { enabled: shouldFetch }
  );

  const error = searchError ;

  // 3. ロジック
  const executeSearch = (inputBiddingNumber?: string) => {
    const searchNum = inputBiddingNumber || targetBiddingNumber;
    if (!searchNum) return;

    // 🔒 D-0127（INV-QBS-2）: 3桁ちょうどのときは先頭に0を補い4桁化する。
    //   状態が4桁になることで、既存の4桁自動検索フロー（shouldFetch・該当なし判定等）にそのまま合流する
    //   （3桁専用の新規トリガー・新規トースト条件は追加しない）。
    const paddedNum = searchNum.length === 3 ? "0" + searchNum : searchNum;

    // 番号をセットする。
    setTargetBiddingNumber(paddedNum);
  };

  useEffect(() => {
    if (shouldFetch && searchResponse) {
      if (searchResponse.isSuccess && searchResponse.item) {
        // データが見つかった場合
        setFoundItem(searchResponse.item);
        setActiveField("price"); // 金額入力へ移動
        toast.dismiss();
      } else if (targetBiddingNumber.length === 4) {
        // 4桁打ったが見つからなかった場合
        setFoundItem(null);
        const errorMsg = searchResponse.message || "該当する出品データがありません";
        toast.error(errorMsg, { id: "search-err" });
      }
    }
  }, [searchResponse, shouldFetch, targetBiddingNumber]); 

  //バリデーションチェック
  const executeBid = () => {
    if (!foundItem) return;
    if (!inputPrice) {
      toast.error("金額を入力してください");
      return;
    }

    const price = Number(inputPrice);

    // 0円が入力された場合は、取消処理(handleBidCancel)を呼び出して終了する
    if (price === 0) {
      handleBidCancel();
      return;
    }

    const minPrice = foundItem.bidMinPrice || 0;
    const multiplier = config?.highBidWarningMultiplier ?? 0;

    // --- ① 判定ロジック：どのメッセージを出すか決める ---
    
    // A. 最低落札価格未満 かつ 入札禁止設定（強制中断）
    if (price < minPrice && config?.prohibitBelowMinimumBid) {
      showConfirm({
        title: pageTitle,
        message: "入力金額が最低落札値未満のため入札できません、金額を確認してください。",
        onConfirm: () => {}
      });
      return;
    }

    let confirmMessage = "入札を確定しますか？"; // デフォルトのメッセージ

    let isWarning = false;
    if (price < minPrice) {
      // B. 最低落札価格未満（警告のみ・許可設定）
      confirmMessage = "入力金額が最低落札値未満ですが、\n入札を確定してよろしいですか？（入札対象にはなりません）";
      isWarning = true;
    } else if (multiplier > 0 && price >= minPrice * multiplier) {
      // C. 高額警告（倍率指定あり かつ 閾値以上）
      confirmMessage = `金額が最低落札値の ${multiplier} 倍以上になっています。\n入札を確定してよろしいですか？`;;
      isWarning = true;
    }

    // --- ② ダイアログ表示 ---
    showConfirm({
      title: pageTitle,
      message: confirmMessage,
      defaultFocus: isWarning ? 'cancel' : 'ok',
      onConfirm: () => {
        // --- ③ 送信処理の実行 ---
        const requestPayload: UpdateBiddingDataRequest = {
          quantity1: foundItem.quantity1 || 0,
          quantity2: foundItem.quantity2 || 0,
          bidPrice: price,
        };

        upsertBidMutation.mutate(
          {
            itemId: foundItem.itemId,
            branchNumber: foundItem.branchNumber,
            requests: requestPayload,
          },
          {
            onSuccess: () => {
              if (isFromListRef.current) {
                // a) 一覧から遷移してきた場合は、一覧画面に戻る
                if (isModal && onClose) {
                  onClose(); // ダイアログなら閉じるだけ
                } else {
                  navigate(-2); // 画面遷移なら今まで通り戻る
                }
              } else {
                // b) かんたん入札を直接開いている場合は、初期化して連続入力に備える
                setTargetBiddingNumber("");
                setInputPrice("");
                setFoundItem(null);
                setActiveField("biddingnumber");
              }
            },
            onError: () => {
              toast.error("送信エラーが発生しました");
            },
          },
        );
      }
    });
  };

  // 取消ボタン（0円で入札する）
  const handleBidCancel = () => {
    if (!foundItem) return;

    showConfirm({
      title: pageTitle,
      message: "入札を取り消しますか？",
      onConfirm: () => {
        const requestPayload: UpdateBiddingDataRequest = {
          quantity1: foundItem.quantity1 || 0,
          quantity2: foundItem.quantity2 || 0,
          bidPrice: 0,
        };

        upsertBidMutation.mutate(
          {
            itemId: foundItem.itemId,
            branchNumber: foundItem.branchNumber,
            requests: requestPayload,
          },
          {
            onSuccess: () => {
              if (isFromListRef.current) {
                // a) 一覧から遷移してきた場合は、一覧画面に戻る
                if (isModal && onClose) {
                  onClose(); // ダイアログなら閉じるだけ
                } else {
                  navigate(-2); // 画面遷移なら今まで通り戻る
                }
              } else {
                // b) かんたん入札を直接開いている場合は、初期化して連続入力に備える
                // クリア処理
                setTargetBiddingNumber("");
                setInputPrice("");
                setFoundItem(null);
                setActiveField("biddingnumber");
              }
            },
            onError: () => {
              toast.error("送信エラーが発生しました");
            },
          },
        );
      }
    });
  };

  //ボタン表示内容判定
  const handleMainAction = () => {
    if (foundItem) {
      executeBid(); 
    } else {
      executeSearch(); 
    }
  };

  //ボタン制御
  const isActionDisabled = foundItem
    ? inputPrice === "" // 未入力（空っぽ）の場合のみ無効にする。0が入力されたら有効
    : !targetBiddingNumber;

  //#region キーパッド関連
  // 数字入力
  const handleNumClick = useCallback((num: string) => {
    if (activeField === "biddingnumber") {
      setIsFromListOrFavorite(false); // 手打ちされたので自動セットモード解除
      setTargetBiddingNumber((prev) => {
        if (prev.length >= 4) return prev;
        return prev + num; // prev(以前の値)を使うことで、依存配列を減らして高速化
      });
    } else {
      setInputPrice((prev) => (prev.length >= 7 ? prev : prev + num));
    }
  }, [activeField]);

  // 削除ボタン
  const handleDelete = useCallback(() => {
    if (activeField === "biddingnumber") {
      setIsFromListOrFavorite(false);
      setFoundItem(null); 
      setTargetBiddingNumber((prev) => prev.slice(0, -1));
      setInputPrice("");
    } else {
      setInputPrice((prev) => prev.slice(0, -1));
    }
  }, [activeField]);

  // クリアボタン
  const handleClear = useCallback(() => {
    if (activeField === "biddingnumber") {
      setIsFromListOrFavorite(false);
      setTargetBiddingNumber("");
      setInputPrice("");
      setFoundItem(null);
    } else {
      setInputPrice("");
    }
  }, [activeField]);  
  //#endregion

  //総量の計算ロジック
  const totalCapacity = useMemo(() => {
    // 1. foundItem がない場合は 0 を返す（ガード）
    if (!foundItem) return 0;

    try {
      // 2. 各値を Big オブジェクトに変換
      const q1 = new Big(foundItem.quantity1 || 0);
      const c1 = new Big(foundItem.capacity1 || 0);
      const q2 = new Big(foundItem.quantity2 || 0);
      const c2 = new Big(foundItem.capacity2 || 0);

      // 3. 10進数で計算を実行: (q1 * c1) + (q2 * c2)
      const total = q1.times(c1).plus(q2.times(c2));

      // 4. 数値型に戻して返却
      return total.toNumber();
    } catch (error) {
      // 不正な文字が入っていた場合などのエラー回避
      console.error("Total capacity calculation error:", error);
      return 0;
    }
  }, [foundItem]);

  // お気に入り一覧の取得
  const { data: favorites } = useGetFavoritesQuery(currentUser?.loginId ?? "");

  // お気に入りを抽出
  const currentFavorites = useMemo(() => {
  if (!favorites) return [];
  
  // 1. 県内・県外・入札会のアイテムを結合する（D-0128・INV-QBS-5）
  const allItems = [
    ...(internalResponse?.items || []),
    ...(externalResponse?.items || []),
    ...(saleResponse?.items || [])
  ];

  if (allItems.length === 0) return [];

  // 2. お気に入りIDリスト(favorites)と照合して抽出
  const filtered = allItems.filter(item => 
    favorites.some(f => f.itemId === item.itemId && f.branchNumber === item.branchNumber)
  );

  // 3. 入札番号順に並び替えて見やすくする
  return filtered.sort((a, b) => (a.biddingNumber || "").localeCompare(b.biddingNumber || ""));
  }, [favorites, internalResponse, externalResponse, saleResponse]);

  // お気に入りから選択した時の処理
  const handleSelectFavorite = (item: BiddingInfo) => {
    // 入札番号が未採番（空文字やnull）の場合は何もしない
    if (!item.biddingNumber || item.biddingNumber === "") 
    {
      return; 
    }
    setIsFromListOrFavorite(true); // お気に入りから来たよという目印
    //　入力欄に番号を表示させる
    setTargetBiddingNumber(item.biddingNumber);

    setFoundItem(item);       // 通信せずに直接セット！
    setActiveField("price");  // 金額入力へ移動

    // お気に入りダイアログを閉じる
    setIsFavoritesOpen(false);
  };

  // 1. Mutation を取得
  const { mutate: toggleFavorite, isPending: isFavoritePending } = useToggleFavoriteMutation(currentUser?.loginId || "");

  // 2. 解除用のハンドラ（行クリックと混ざらないように stopPropagation する）
  const handleRemoveFavorite = (e: React.MouseEvent, item: BiddingInfo) => {
    e.stopPropagation(); // 行選択（入札番号入力）が動かないようにする
    if (isFavoritePending) return;

    //確認ダイアログ
    // 入札番号があればそれを、なければ荷印を表示に使用
    const displayName = item.biddingNumber 
      ? `入札番号 ${item.biddingNumber}` 
      : (item.brand || "選択した商品");

    const message = `「${displayName}」をお気に入りから解除しますか？`;

    showConfirm({
      title: pageTitle,
      message: message,
      onConfirm: () => {
        toggleFavorite({ 
          itemId: item.itemId, 
          branchNumber: item.branchNumber 
        });
      }
    });
  };

 // 🔒 D-0127: 入札会の入札日取得（isSaleDateLoading）が終わる前に操作可能になると、
 //   直後に入札会番号を打った場合だけ検索がサイレントに空振りする（activeSaleBiddingDate未取得のため）。
 //   通常入札の日付取得と同様に、こちらの完了も待ってから操作可能にする。
 const isPageLoading = isMastersLoading || isDateLoading || isSaleDateLoading || !isInitialized;
  
  if (isPageLoading) 
  {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500 font-bold">データを準備中...</p>
        </div>
      </div>
    );
  }
  if (error) 
  {
    return ( 
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="bg-red-100 text-red-700 p-4 rounded-md">エラー: {error.message}</div>
      </div> 
    );
  }

  // 4. レンダリング
  return (
    //CommonLayoutの余白の中に配置
    <div className={`w-full font-sans text-slate-800 flex flex-col relative ${isModal ? 'h-[calc(100dvh-0.5rem)] md:max-h-[800px]' : 'h-[calc(100dvh-6rem)]'}`}>
      {isModal && (
         <div className="flex justify-end p-2 pb-1 bg-white rounded-t-2xl">
           <button 
             onClick={onClose}
             className="px-4 py-1.5 text-sm font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 active:scale-95 rounded-full transition-all shadow-sm"
           >
             戻る
           </button>
         </div>
      )}

      <Toaster position="top-center" />

      {/* 共通背景レイヤー */}
      {OverlayElement}

      {/* 白いパネル */}
      <div className="flex-1 flex flex-col bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-200 relative">
        <div className="shrink flex flex-col p-1.5 gap-1 min-h-0">
            <div className="px-1">
            {!isModal && (
            <button
              onClick={() => setIsFavoritesOpen(true)}
              className="flex-1 w-full flex items-center justify-center gap-2 h-8 bg-white text-rose-500 border border-rose-100 rounded-xl active:bg-slate-50 active:scale-[0.98] transition-all shadow-sm group"
            >
              <Heart 
                className={`w-5 h-5 transition-colors ${
                  favorites && favorites.length > 0 ? "fill-rose-500" : ""
                }`} 
              />
              <span className="text-[14px] font-black whitespace-nowrap">
                お気に入りから選択
              </span>
            </button>
            )}
          </div>
          {/* 入力欄グループ */}
          <div className="flex flex-col gap-0.5 shrink-0">
            {/* 入札番号入力 */}
            <div
              onClick={() => setActiveField("biddingnumber")}
              className={`flex-none rounded-xl px-3 border-2 transition-all cursor-pointer relative flex items-center gap-2 h-10 [@media(min-height:700px)]:h-16
              ${activeField === "biddingnumber" ? "border-indigo-500 bg-indigo-50" : "border-slate-100 bg-slate-50"}`}
            >
              <label className="block text-[15px] [@media(min-height:700px)]:text-lg font-bold text-slate-500 shrink-0 transition-all">
                入札番号:
              </label>
              <div className="relative w-full flex items-center">
                <input
                  type="text"
                  readOnly
                  value={targetBiddingNumber}
                  className="w-full text-[24px] [@media(min-height:700px)]:text-3xl font-bold bg-transparent outline-none text-slate-900 px-1 pr-8 transition-all"
                />
                {/* 入力中のみ×（クリアボタン）を表示する */}
                {targetBiddingNumber && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); //親の処理を動かさないように
                      setTargetBiddingNumber(""); //入札番号クリア
                      setInputPrice(""); //入札額クリア
                      setFoundItem(null); //出品データ非表示にする
                      setActiveField("biddingnumber"); //フォーカス移動させる
                    }}
                    className="absolute right-0 p-1 [@media(min-height:700px)]:p-1.5 bg-slate-300 rounded-full text-slate-800 hover:bg-slate-500 transition-all"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* 入札額入力 */}
            <div
              onClick={() => setActiveField("price")}
              className={`flex-none rounded-xl px-3 border-2 transition-all cursor-pointer relative flex items-center gap-2 h-12 [@media(min-height:700px)]:h-16
              ${activeField === "price" ? "border-indigo-500 bg-indigo-50" : "border-slate-100 bg-slate-50"}`}
            >
              <label className="block text-[15px] [@media(min-height:700px)]:text-lg font-bold text-slate-500 shrink-0 transition-all">
                入札額(円):
              </label>
              <div className="relative w-full flex items-center">
                <NumericFormat
                  value={inputPrice}
                  displayType="input"
                  thousandSeparator={true}
                  readOnly
                  placeholder={
                    foundItem?.bidPrice
                      ? `現在入札額 ${foundItem.bidPrice.toLocaleString()}`
                      : ""
                  }
                  className="w-full text-[24px] [@media(min-height:700px)]:text-3xl font-bold bg-transparent outline-none text-black-600 px-1 text-right pr-8 placeholder:text-slate-400 placeholder:text-lg [@media(min-height:700px)]:placeholder:text-[24px] transition-all"
                />
                {inputPrice && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setInputPrice("");
                      setActiveField("price");
                    }}
                    className="absolute right-0 p-1 [@media(min-height:700px)]:p-1.5 bg-slate-300 rounded-full text-slate-800 hover:bg-slate-500 transition-all "
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* 詳細表示エリア */}
          <div className="min-h-0 overflow-y-auto flex flex-col justify-center py-1 shrink">
            <BiddingItemNumberDetail
              foundItem={foundItem}
              onBidCancel={handleBidCancel}
              totalCapacity={totalCapacity}
            />
          </div>

          {/* ボタン */}
          <button
            onClick={handleMainAction}
            disabled={isActionDisabled}
            className={`flex-none w-full h-10 [@media(min-height:700px)]:h-14 rounded-2xl text-lg [@media(min-height:700px)]:text-xl font-black text-white shadow-md transition-all active:scale-[0.98] flex items-center justify-center gap-1.5 shrink-0 
              ${
                isActionDisabled
                  ? "bg-slate-400 opacity-50 cursor-not-allowed" // ① 無効な時：グレー
                  : foundItem
                    ? "bg-green-600 hover:bg-green-700" // ② 入札確定の時：緑
                    : "bg-indigo-600 hover:bg-indigo-700" // ③ 検索の時：青
              }
            `}
          >
            {foundItem ? (
              // <> <Check className="w-6 h-6" /> 入札確定 </>
              <>
                <Check className="w-5 h-5 [@media(min-height:700px)]:w-6 [@media(min-height:700px)]:h-6" />{" "}
                入札確定
              </>
            ) : (
              // <> <Search className="w-6 h-6" /> 出品データ検索 </>
              <>
                <Search className="w-5 h-5 [@media(min-height:700px)]:w-6 [@media(min-height:700px)]:h-6" />{" "}
                出品データ検索
              </>
            )}
          </button>
        </div>

        {/* キーパッド */}
        {/* <div className="flex-1 bg-slate-200 border-t border-slate-200 pb-safe min-h-[180px]"> */}
        <div className="flex-1 bg-slate-200 border-t border-slate-200 pb-safe min-h-[240px] [@media(min-height:700px)]:min-h-[280px]">
          <NumericKeypad
            onNumClick={handleNumClick}
            onClear={handleClear}
            onDelete={handleDelete}
            // Enterが押されたとき、ボタンが無効でなければメインアクションを実行
            onEnter={() => {
              // ボタンが無効じゃない ＆ 確認ダイアログが開いていない ＆ お気に入り画面が開いていない 時だけ実行
              if (!isActionDisabled && !isConfirmOpen && !isFavoritesOpen) {
                handleMainAction();
              }
            }}
        />
        </div>
        {/* お気に入り選択ポップアップダイアログ */}
        {isFavoritesOpen && (
          <div 
            className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200"
            onClick={() => setIsFavoritesOpen(false)}
          >
            <div 
              className="bg-white rounded-2xl w-full max-w-sm max-h-[75vh] flex flex-col shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-200"
              onClick={(e) => e.stopPropagation()}
            >
              {/* ヘッダー：スッキリした白ベース */}
              <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-white">
                <div className="flex items-center gap-2.5">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full border border-slate-100 shadow-sm">
                    <Heart className="w-5 h-5 fill-rose-500 text-rose-500" />
                  </div>
                  <div>
                    <h3 className="font-black text-slate-800 text-sm leading-none">お気に入りから選択</h3>
                  </div>
                </div>
                <button 
                  onClick={() => setIsFavoritesOpen(false)} 
                  className="p-1 text-slate-500 hover:text-slate-500 hover:bg-slate-50 rounded-full transition-all"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              {/* コンテンツエリア */}
              <div className="overflow-y-auto p-3 flex flex-col gap-2 bg-slate-50/30">
                {isListLoading ? (
                  // ① 読み込み中はこのブロックだけを表示（チラつき防止）
                  <div className="py-20 text-center flex flex-col items-center gap-2">
                    <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                    <p className="text-xs font-bold text-slate-400">情報を取得中...</p>
                  </div>
                ) : currentFavorites.length > 0 ? (
                  // ② 読み込みが終わってデータがある場合
                  currentFavorites.map((fav) => (
                    <div key={`${fav.itemId}_${fav.branchNumber}`} className="relative group">
                      <button
                        onClick={() => handleSelectFavorite(fav)}
                        className="w-full flex items-center justify-between p-3 bg-white border border-slate-200 rounded-xl active:bg-slate-50 transition-all hover:border-slate-300 pr-10 shadow-sm"
                      >
                        {/* ...ボタンの中身は今のままでOK... */}
                        <div className="flex flex-col items-start min-w-0 flex-1">
                          <span className="text-[12px] font-black bg-slate-800 text-white px-2 py-0.5 rounded-full mb-1">
                            No.{fav.biddingNumber}
                          </span>
                          <span className="font-[14px] text-slate-800 truncate w-full text-left text-base leading-tight">
                            {fav.brand || "-"}
                          </span>
                          <div className="flex flex-col text-left gap-0.5 w-full mt-1">
                            <div className="text-[14px] font-bold text-slate-700 leading-tight">{fav.sellerName || "-"}</div>
                            <div className="text-[14px] font-bold text-slate-700 leading-tight">{!fav.isFarmerHidden ? (fav.farmerName || "-") : "-"}</div>
                          </div>
                        </div>
                        <span className="text-[16px] font-black text-rose-600">
                          {fav.bidPrice > 0 ? `${fav.bidPrice.toLocaleString()}円` : "未入札"}
                        </span>
                      </button>
                  
                      <button
                        onClick={(e) => handleRemoveFavorite(e, fav)}
                        disabled={isFavoritePending}
                        className="absolute top-1.5 right-1.5 p-2 rounded-full hover:bg-rose-50 transition-colors z-10"
                      >
                        <X className="w-5 h-5 text-slate-500" />
                      </button>
                    </div>
                  ))
                ) : (
                  // ③ 読み込みが終わってデータが本当に空の場合
                  <div className="py-16 text-center flex flex-col items-center">
                    <Heart className="w-12 h-12 text-slate-100 mb-3" />
                    <p className="text-sm font-bold text-slate-400">
                      現在の条件に合うお気に入りはありません。
                    </p>
                  </div>
                )}
              </div>

              {/* フッター */}
              <div className="p-3 bg-white border-t border-slate-100">
                <button 
                  onClick={() => setIsFavoritesOpen(false)}
                  className="w-full py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold rounded-xl transition-colors text-sm"
                >
                  閉じる
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BiddingInputNumberPage;